import { Alert, Box } from '@mui/material';
import { useMemo } from 'react';
import { UseFormResetField, useWatch } from 'react-hook-form';
import { Control, FieldErrors } from 'react-hook-form';

import { GroupedSelectFormField } from '@/components/Form/GroupedSelect';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import { IndiciaReasonCode } from '@/types/api/tax-info';

import { getDataTestId } from '@/utils/browser';

import { OtherCountriesForeignIndiciaSectionProps } from '../TaxInfoDrawer.interface';
import { OtherCountriesTaxFormValues } from '../utils/types';
import { FOREIGN_INDICIA_REASON_OTHER_MAX_LENGTH } from './constants';
import { getForeignIndiciaGroupedSelectConfig } from './utils';

import {
  TaxInfoFieldLabel,
  TaxInfoQuestionLabel,
  TaxInfoQuestionSection,
  TaxInfoSubSection,
} from '../TaxInfoDrawer.styled';

type ForeignIndiciaCountryFieldProps = {
  countryCode: string;
  countryName: string;
  control: Control<OtherCountriesTaxFormValues>;
  errors: FieldErrors<OtherCountriesTaxFormValues>;
  isPending: boolean;
  resetField: UseFormResetField<OtherCountriesTaxFormValues>;
};

function ForeignIndiciaCountryField({
  countryCode,
  countryName,
  control,
  errors,
  isPending,
  resetField,
}: Readonly<ForeignIndiciaCountryFieldProps>) {
  const { t } = useTranslate();
  const { groups, otherOption } = useMemo(
    () => getForeignIndiciaGroupedSelectConfig(t),
    [t],
  );
  const situationFieldName =
    `indiciaReasonCode.${countryCode}` as `indiciaReasonCode.${string}`;
  const otherFieldName =
    `indiciaReasonOther.${countryCode}` as `indiciaReasonOther.${string}`;
  const selectedReason = useWatch({
    control,
    name: situationFieldName,
  });
  const otherReason = useWatch({
    control,
    name: otherFieldName,
  });
  const situationError = errors.indiciaReasonCode?.[countryCode];
  const otherReasonError = errors.indiciaReasonOther?.[countryCode];
  const showOtherReasonField = selectedReason === IndiciaReasonCode.OTHER;
  const otherReasonCharactersLeft =
    FOREIGN_INDICIA_REASON_OTHER_MAX_LENGTH - (otherReason?.length ?? 0);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={1} mb={2}>
      <TaxInfoFieldLabel variant="caption">
        {t('taxInfoDrawer.question2.foreignIndicia.countryQuestion', {
          country: countryName,
        })}
      </TaxInfoFieldLabel>

      <GroupedSelectFormField
        control={control}
        name={situationFieldName}
        selectionMode="single"
        title={t('taxInfoDrawer.question2.foreignIndicia.reason.placeholder')}
        placeholder={t(
          'taxInfoDrawer.question2.foreignIndicia.reason.placeholder',
        )}
        groups={groups}
        otherOption={otherOption}
        disabled={isPending}
        error={!!situationError}
        helperText={situationError?.message}
        onSelectChange={selectedValue => {
          if (selectedValue !== IndiciaReasonCode.OTHER) {
            resetField(otherFieldName, { defaultValue: '' });
          }
        }}
      />

      {showOtherReasonField && (
        <TextInput
          name={otherFieldName}
          control={control}
          label={t('taxInfoDrawer.question2.foreignIndicia.reasonOther.label')}
          disabled={isPending}
          error={!!otherReasonError}
          helperText={
            otherReasonError?.message ??
            t(
              'taxInfoDrawer.question2.foreignIndicia.reasonOther.charactersLeft',
              { count: otherReasonCharactersLeft },
            )
          }
          inputProps={{
            maxLength: FOREIGN_INDICIA_REASON_OTHER_MAX_LENGTH,
          }}
          {...getDataTestId(
            `tax-info-foreign-indicia-reason-other-${countryCode}`,
          )}
        />
      )}
    </Box>
  );
}

export function OtherCountriesForeignIndiciaSection({
  control,
  errors,
  foreignIndiciaCountries,
  isPending,
  resetField,
}: Readonly<OtherCountriesForeignIndiciaSectionProps>) {
  const { t, country: translatedCountry } = useTranslate();

  return (
    <TaxInfoSubSection>
      <Alert
        icon={<Icon name="alert-outline" />}
        color="warning"
        variant="standard"
        {...getDataTestId('tax-info-foreign-indicia-warning')}
      >
        {t('taxInfoDrawer.question2.foreignIndicia.warning')}
      </Alert>

      <TaxInfoQuestionLabel variant="body1Medium">
        {t('taxInfoDrawer.question2.foreignIndicia.sectionTitle')}
      </TaxInfoQuestionLabel>
      <TaxInfoQuestionSection>
        {foreignIndiciaCountries.map(({ countryCode }) => (
          <ForeignIndiciaCountryField
            key={countryCode}
            countryCode={countryCode}
            countryName={translatedCountry[countryCode] ?? countryCode}
            control={control}
            errors={errors}
            isPending={isPending}
            resetField={resetField}
          />
        ))}
      </TaxInfoQuestionSection>
    </TaxInfoSubSection>
  );
}
